import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../api';
import FOOTBALL_CLUBS from '../footballClubs';

// עונת הקלף הנוכחית — עוגן עונתי
const SEASON_LABEL = '26/27';

const POSITIONS = ['שוער','בלם','מגן','קשר','כנף','חלוץ'];

// טקסט ההצטרפות — נוסח אחיד בכל העמוד
const JOIN_LINE = 'ההצטרפות למועדון בחינם · קלף הכוכבים במתנה · 20 ₪ דמי משלוח עד הבית';

const YT_ICON = <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1C4.5 20.5 12 20.5 12 20.5s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/></svg>;
const WA_ICON = <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.847L.057 23.882l6.197-1.448A11.934 11.934 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.894a9.876 9.876 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374A9.867 9.867 0 0 1 2.106 12C2.106 6.535 6.535 2.106 12 2.106c5.466 0 9.894 4.429 9.894 9.894 0 5.466-4.428 9.894-9.894 9.894z"/></svg>;
const PHONE_ICON = <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>;

// כפתור הצטרפות בולט (Hero / קלף / טופס)
function JoinButton({ style }) {
  return (
    <a href="#register" style={{
      display:'inline-flex', flexDirection:'column', alignItems:'center', gap:4,
      background:'linear-gradient(135deg,#d4af37,#f0cc60)', color:'#0a1120', fontWeight:900,
      fontSize:'clamp(1rem,3vw,1.15rem)', padding:'16px 32px', borderRadius:16,
      textDecoration:'none', boxShadow:'0 6px 32px rgba(212,175,55,0.55)',
      textAlign:'center', lineHeight:1.3, ...style,
    }}>
      <span>⭐ אני רוצה את קלף הכוכבים לילד שלי</span>
      <span style={{fontSize:'clamp(0.78rem,2vw,0.88rem)', fontWeight:700, opacity:0.85, marginTop:2}}>{JOIN_LINE}</span>
    </a>
  );
}

// ── שדה חיפוש/השלמה אוטומטית של מועדון ──────────────────────
function TeamCombobox({ value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen]   = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // סגירה בלחיצה מחוץ לרכיב
  useEffect(() => {
    const onDoc = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim();
  const matches = q
    ? FOOTBALL_CLUBS.filter(c => c.includes(q)).slice(0, 8)
    : FOOTBALL_CLUBS.slice(0, 8);
  const options = [...matches, 'אחר (לא ברשימה)'];

  const pick = val => {
    const clean = val === 'אחר (לא ברשימה)' ? 'אחר' : val;
    onChange(clean);
    setQuery(clean);
    setOpen(false);
  };

  const onKey = e => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && options[active]) pick(options[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const inputBase = {
    width:'100%', padding:'10px 40px 10px 14px', background:'#1a2a4a', color:'#fff',
    border:`1px solid ${open ? '#d4af37' : '#2a3a6a'}`, borderRadius:8, fontSize:'1rem',
    direction:'rtl', boxSizing:'border-box', fontFamily:"'Heebo', sans-serif",
  };

  return (
    <div ref={boxRef} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:'#d4af37' }}>🔍</span>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="הקלידו שם מועדון — לדוגמה: פתח"
          autoComplete="off"
          style={inputBase}
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); onChange(''); setOpen(true); }}
            style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#8a9aba', cursor:'pointer', fontSize:'1.1rem' }}>×</button>
        )}
      </div>
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', right:0, left:0, zIndex:30,
          background:'#0d1a35', border:'1px solid #2a3a6a', borderRadius:10, overflow:'hidden',
          boxShadow:'0 12px 40px rgba(0,0,0,0.6)', maxHeight:280, overflowY:'auto',
        }}>
          {matches.length === 0 && q && (
            <div style={{ padding:'10px 14px', color:'#8a9aba', fontSize:'0.85rem' }}>לא נמצא מועדון בשם זה</div>
          )}
          {options.map((opt, i) => {
            const isOther = opt === 'אחר (לא ברשימה)';
            return (
              <div key={opt}
                onMouseDown={e => { e.preventDefault(); pick(opt); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding:'10px 14px', cursor:'pointer', fontSize:'0.95rem',
                  color: isOther ? '#8a9aba' : '#fff',
                  background: active === i ? 'rgba(212,175,55,0.15)' : 'transparent',
                  borderTop: isOther ? '1px solid #1e2e5a' : 'none',
                  fontStyle: isOther ? 'italic' : 'normal',
                }}>
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Home({ scrollToForm = false }) {
  const [form, setForm] = useState({
    first_name:'', last_name:'',
    phone:'', email:'',
    child_first_name:'', child_last_name:'',
    child_dob_day:'', child_dob_month:'', child_dob_year:'',
    team:'',
  });
  const [positions, setPositions] = useState([]);
  const [agreed1, setAgreed1] = useState(false);
  const [agreed2, setAgreed2] = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // /join — קישור נפרד שנוחת ישירות על טופס ההצטרפות.
  // התמונות נטענות אחרי הגלילה ומזיזות את הפריסה — לכן מיישרים כמה פעמים.
  useEffect(() => {
    if (!scrollToForm) return;
    const go = () => document.getElementById('register')?.scrollIntoView({ block:'start' });
    go();
    // התמונות נטענות אחרי הגלילה ומזיזות את הפריסה — מיישרים מחדש עד שהיא מתייצבת
    const ro = new ResizeObserver(go);
    ro.observe(document.body);
    const stop = setTimeout(() => ro.disconnect(), 6000);
    return () => { ro.disconnect(); clearTimeout(stop); };
  }, [scrollToForm]);

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const togglePosition = pos => {
    if (positions.includes(pos)) setPositions(positions.filter(p => p !== pos));
    else if (positions.length < 3) setPositions([...positions, pos]);
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.child_dob_year) { setError('יש למלא את שנת הלידה של השחקן/ית'); return; }
    if (!agreed1 || !agreed2) { setError('יש לאשר את שני תנאי ההצטרפות'); return; }
    setError('');
    setLoading(true);
    try {
      const payload = {
        first_name:  (form.first_name + ' ' + form.last_name).trim(),
        last_name:   form.last_name,
        phone:       form.phone,
        email:       form.email || null,
        child_name:  (form.child_first_name + ' ' + form.child_last_name).trim(),
        child_last_name: form.child_last_name,
        child_dob:   form.child_dob_day && form.child_dob_month && form.child_dob_year
          ? `${form.child_dob_year}-${String(form.child_dob_month).padStart(2,'0')}-${String(form.child_dob_day).padStart(2,'0')}`
          : (form.child_dob_year ? `${form.child_dob_year}-01-01` : null),
        team:        form.team,
        birth_year:  form.child_dob_year,   // שנת הלידה — הנתון הקריטי במערכת
        positions:   positions.join(', '),
      };
      const { data } = await axios.post(API_BASE + '/members', payload);
      // תמונה, כתובת ותשלום (ביט/פייבוקס) מתואמים בוואטסאפ ביוזמת ההורה אחרי ההרשמה
      navigate('/card/' + data.id);
    } catch(err) {
      setError(err.response?.data?.error || 'שגיאה, נסה שוב');
    } finally { setLoading(false); }
  };

  return (
    <div>

      {/* ===== NAVBAR ===== */}
      <nav className="navbar" style={{justifyContent:'center'}}>
        <a href="/" style={{textDecoration:'none', display:'block', textAlign:'center'}}>
          <span style={{
            color:'#d4af37', fontWeight:700, fontSize:'clamp(0.8rem, 2.5vw, 1.1rem)',
            fontFamily:"'Heebo', sans-serif", whiteSpace:'nowrap', letterSpacing:'0.01em'
          }}>
            ⚽ ליגת הכוכבים של יוסטאר למשפחות שחיות כדורגל
          </span>
        </a>
      </nav>

      {/* ===== HERO ===== */}
      <div className="hero" style={{backgroundImage:"url('/hero-main.png')", backgroundSize:'cover', backgroundPosition:'center top', position:'relative'}}>
      </div>
      <div style={{background:'linear-gradient(180deg,#0a1530,#060d1e)', padding:'40px 20px 48px', direction:'rtl', textAlign:'center'}}>
        <h1 style={{color:'#fff', fontWeight:900, fontSize:'clamp(1.5rem,5vw,2.4rem)', margin:'0 0 16px', lineHeight:1.25}}>
          ליגת הכוכבים של יוסטאר
        </h1>
        <p style={{color:'#c8b870', fontSize:'clamp(0.9rem,2.2vw,1.05rem)', fontWeight:400, lineHeight:1.75, marginBottom:22, maxWidth:560, margin:'0 auto 22px'}}>
          לכל הורה שעומד על הקווים, רואה את הילד שלו נותן הכל —<br/>
          <strong style={{color:'#fff'}}>ורוצה שהרגע הזה לא ייעלם.</strong>
        </p>
        {/* עוגן עונתי */}
        <div style={{display:'inline-flex', alignItems:'center', gap:8, background:'rgba(212,175,55,0.12)', border:'1px solid rgba(212,175,55,0.45)', borderRadius:30, padding:'7px 18px', color:'#f0cc60', fontWeight:800, fontSize:'clamp(0.82rem,2.2vw,0.95rem)', marginBottom:26}}>
          🗓️ קלף עונת {SEASON_LABEL} — בדרך אליכם
        </div>
        <br/>
        <JoinButton />
      </div>

      {/* ===== כוכבי יוסטאר (הועבר לכאן — מיד אחרי ה-Hero) ===== */}
      <div className="about-ustar-section" style={{backgroundImage:"url('/bg2.jpg')"}}>
        <div className="about-ustar-overlay">
          <div className="about-ustar-content">
            <h2>⭐ כוכבי יוסטאר — הדרך לחלום מתחילה כאן</h2>
            <p style={{color:'#e8edf5', fontSize:'1rem', lineHeight:1.8, marginBottom:32, textAlign:'center', maxWidth:720, margin:'0 auto 32px', textShadow:'0 1px 4px rgba(0,0,0,0.8)'}}>
              שחקנים שצילמנו בילדותם — ועכשיו משחקים בקבוצות הגדולות בעולם.
              <br/><strong style={{color:'#f0cc60'}}>אלה הם ילדים שהורים כמוכם בחרו לתעד את הדרך שלהם.</strong>
            </p>

            <div style={{margin:'32px 0'}}>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:20}}>
                {[
                  {name:'אוסקר גלוך', club:'אייאקס 🇳🇱', vid:'ML0BsC7WVdg'},
                  {name:'אוריאן גורן', club:'ברצלונה 🇪🇸', vid:'gDDc5JHjbio'},
                  {name:'מנור סולומון', club:'ווסטהאם 🏴󠁧󠁢󠁥󠁮󠁧󠁿', vid:'NnA-toEkRiQ'},
                ].map(({name, club, vid}) => (
                  <a key={vid} href={`https://www.youtube.com/watch?v=${vid}`} target="_blank" rel="noopener noreferrer"
                    style={{display:'block', borderRadius:14, overflow:'hidden', border:'2px solid #1e2e5a',
                      boxShadow:'0 8px 32px rgba(0,0,0,0.5)', textDecoration:'none', background:'#0d1a35',
                      transition:'transform 0.2s, box-shadow 0.2s'}}
                    onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 16px 48px rgba(212,175,55,0.25)';}}
                    onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='0 8px 32px rgba(0,0,0,0.5)';}}
                  >
                    <div style={{position:'relative', paddingBottom:'56.25%', background:'#000'}}>
                      <img src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`} alt={name}
                        style={{position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover'}} />
                      <div style={{position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                        width:52, height:52, borderRadius:'50%', background:'rgba(212,175,55,0.9)',
                        display:'flex', alignItems:'center', justifyContent:'center'}}>
                        <svg viewBox="0 0 24 24" fill="#0a1120" width="24" height="24"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                    <div style={{padding:'12px 16px', textAlign:'right'}}>
                      <div style={{color:'#fff', fontWeight:700, fontSize:'1rem', marginBottom:3}}>{name}</div>
                      <div style={{color:'#d4af37', fontSize:'0.85rem', fontWeight:600}}>{club}</div>
                      <div style={{display:'flex', alignItems:'center', gap:5, marginTop:8, color:'#e00', fontSize:'0.8rem', justifyContent:'flex-end'}}>
                        <svg viewBox="0 0 24 24" fill="#e00" width="16" height="16"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1C4.5 20.5 12 20.5 12 20.5s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/></svg>
                        צפו בקליפ ביוטיוב
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== קלף הכוכבים של יוסטאר ===== */}
      <div style={{background:'linear-gradient(180deg,#060d1e 0%,#0a1a3e 100%)', padding:'60px 20px', direction:'rtl'}}>
        <div style={{maxWidth:900, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:40}}>
            <div style={{display:'inline-block', background:'rgba(212,175,55,0.1)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:30, padding:'5px 18px', color:'#d4af37', fontSize:'0.8rem', fontWeight:700, letterSpacing:1, marginBottom:14}}>⭐ מתנת ההצטרפות למועדון</div>
            <h2 style={{color:'#fff', fontWeight:900, fontSize:'clamp(1.4rem,4vw,2rem)', margin:'0 0 10px'}}>⭐ קלף הכוכבים של יוסטאר</h2>
            <div style={{color:'#f0cc60', fontWeight:800, fontSize:'clamp(0.9rem,2.2vw,1rem)', marginBottom:16}}>🗓️ מהדורת עונת {SEASON_LABEL}</div>
            <p style={{color:'#c0cce6', fontSize:'clamp(0.95rem,2.2vw,1.05rem)', lineHeight:1.8, maxWidth:680, margin:'0 auto'}}>
              מגשימים חלום: קלף כדורגל אישי שמציג את הילד שלכם כמו השחקנים הגדולים באמת!
            </p>
          </div>

          <div style={{display:'flex', flexWrap:'wrap', gap:36, alignItems:'center', justifyContent:'center', marginBottom:40}}>
            <div style={{flexShrink:0, textAlign:'center'}}>
              <img src="/star-card-photo.png" alt="קלף הכוכבים של יוסטאר"
                style={{width:240, borderRadius:12, boxShadow:'0 12px 48px rgba(212,175,55,0.45)', border:'2px solid rgba(212,175,55,0.4)'}} />
            </div>
            <div style={{flex:'1 1 280px', maxWidth:480}}>
              <p style={{color:'#c0cce6', fontSize:'clamp(0.88rem,2vw,0.97rem)', lineHeight:1.85, marginBottom:20}}>
                אנחנו לוקחים תמונה מנצחת של הילד מהמגרש, משלבים את הנתונים האישיים שלו, ואורזים הכל לעיצוב כדורגל ייחודי ומקצועי מבית יוסטאר.
                זו מזכרת מלהיבה שכל שחקן צעיר חולם להחזיק ביד ולהשוויץ לחברים.
              </p>
            </div>
          </div>
        </div>

        <div style={{textAlign:'center', padding:'32px 20px 48px'}}>
          <JoinButton />
        </div>
      </div>

      {/* ===== הטבות חברי המועדון (סקשן חדש) ===== */}
      <div style={{background:'linear-gradient(180deg,#080f20,#060c1a)', padding:'56px 20px', direction:'rtl'}}>
        <div style={{maxWidth:860, margin:'0 auto'}}>
          <h2 style={{color:'#fff', fontWeight:900, fontSize:'clamp(1.4rem,4vw,2rem)', textAlign:'center', margin:'0 0 32px'}}>⭐ מה מקבלים חברי המועדון</h2>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:16}}>
            {[
              {icon:'🎁', title:'קלף הכוכבים האישי במתנה', text:'קלף כדורגל מעוצב עם התמונה והנתונים של הילד, כולל סטנד. דמי משלוח בלבד.'},
              {icon:'📲', title:'תכנים להורים', text:'מה מאמנים מחפשים, איך מעברים עובדים, ומה חשוב לדעת בכל שכבת גיל.'},
              {icon:'⭐', title:'מחיר חבר מועדון', text:'הטבות ומחירים מיוחדים על מוצרי יוסטאר.'},
            ].map(({icon, title, text}) => (
              <div key={title} style={{background:'#0d1a35', border:'1px solid #1e2e5a', borderRadius:16, padding:'22px 20px', textAlign:'right'}}>
                <div style={{fontSize:'1.8rem', marginBottom:10}}>{icon}</div>
                <div style={{color:'#d4af37', fontWeight:800, fontSize:'1.02rem', marginBottom:8}}>{title}</div>
                <div style={{color:'#b0bcd0', fontSize:'0.9rem', lineHeight:1.7}}>{text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== על יוסטאר (מקוצר) ===== */}
      <div style={{background:'linear-gradient(180deg,#060c1a,#080f20)', padding:'48px 20px', direction:'rtl'}}>
        <div style={{maxWidth:720, margin:'0 auto', textAlign:'center'}}>
          <div style={{display:'inline-block', background:'rgba(212,175,55,0.1)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:30, padding:'6px 22px', color:'#d4af37', fontSize:'0.8rem', fontWeight:700, marginBottom:16, letterSpacing:1}}>⚽ על יוסטאר</div>
          <h2 style={{color:'#fff', fontWeight:900, fontSize:'clamp(1.3rem,3.5vw,1.8rem)', lineHeight:1.35, marginBottom:16}}>
            יוסטאר — מלווים את שחקני העתיד של ישראל 🇮🇱
          </h2>
          <p style={{color:'#c8d0e0', fontSize:'clamp(0.95rem,2.2vw,1.05rem)', lineHeight:1.9, margin:'0 0 16px'}}>
            יוסטאר מייצרת קליפי ביצועים אישיים לשחקני כדורגל צעירים — סרטונים ערוכים שמציגים את היכולות של השחקן על המגרש.
          </p>
          <p style={{color:'#c8d0e0', fontSize:'clamp(0.95rem,2.2vw,1.05rem)', lineHeight:1.9, margin:0}}>
            מעל 16 שנה במגרשי הכדורגל בישראל. תיעדנו שחקנים כשהיו ילדים —
            <strong style={{color:'#d4af37'}}> חלקם משחקים היום באייאקס, בברצלונה ובווסטהאם.</strong>
          </p>
        </div>
      </div>

      {/* ===== REGISTER FORM ===== */}
      <div className="form-section" id="register" style={{backgroundImage:"url('/stadium-dark.jpg')"}}>
        <div className="section-inner">
          <div className="form-box">
            <h2>⭐ טופס הצטרפות לליגת הכוכבים של יוסטאר</h2>
            <p className="subtitle" style={{color:'#d4af37', fontWeight:700, textShadow:'0 2px 12px rgba(212,175,55,0.3)', lineHeight:1.9}}>לכל הורה שעומד על הקווים, רואה את הילד שלו נותן הכל —<br/><span style={{fontSize:'1.1em'}}>⭐</span><br/>ורוצה שהרגע הזה לא ייעלם.</p>
            <div style={{background:'rgba(212,175,55,0.1)', border:'1px solid rgba(212,175,55,0.3)', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:'0.9rem', color:'#d4af37', textAlign:'center'}}>
              🎁 {JOIN_LINE}
            </div>

            {/* הטופס מיועד להורה + שיתוף לשחקן */}
            <div style={{background:'rgba(122,176,255,0.08)', border:'1px solid rgba(122,176,255,0.3)', borderRadius:10, padding:'14px 16px', marginBottom:16, textAlign:'center'}}>
              <div style={{color:'#c0cce6', fontSize:'0.9rem', lineHeight:1.7, marginBottom:12}}>
                👨‍👩‍👦 הטופס מיועד להורה.
                <br/>הקלף נשלח לבית ומתואם בוואטסאפ מול ההורה.
              </div>
              <a href={`https://wa.me/?text=${encodeURIComponent('הצטרפו לליגת הכוכבים של יוסטאר וקבלו את קלף הכוכבים של הילד/ה במתנה ⭐\nהרשמה כאן: https://www.ustarclub.co.il/ustarleague')}`}
                target="_blank" rel="noopener noreferrer"
                style={{display:'inline-flex', alignItems:'center', gap:8, background:'rgba(37,211,102,0.14)', border:'1px solid rgba(37,211,102,0.4)', color:'#25d366', borderRadius:10, padding:'9px 18px', textDecoration:'none', fontWeight:700, fontSize:'0.88rem'}}>
                💬 שחקן/ית? שלחו את הקישור להורים
              </a>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={submit}>

              {/* ── שלב 1: פרטי ההורה ── */}
              <div className="form-step-label"><span className="step-num">1</span> פרטי ההורה</div>
              <div className="form-row">
                <div className="form-group">
                  <label>שם פרטי *</label>
                  <input name="first_name" value={form.first_name} onChange={handle} required placeholder="ישראל" />
                </div>
                <div className="form-group">
                  <label>שם משפחה *</label>
                  <input name="last_name" value={form.last_name} onChange={handle} required placeholder="ישראלי" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>טלפון נייד *</label>
                  <input name="phone" value={form.phone} onChange={handle} required type="tel" placeholder="05X-XXXXXXX" />
                </div>
                <div className="form-group">
                  <label>כתובת אימייל</label>
                  <input name="email" value={form.email} onChange={handle} type="email" placeholder="example@email.com (לא חובה)" />
                </div>
              </div>

              {/* ── שלב 2: פרטי השחקן/ית ── */}
              <div className="form-step-label" style={{marginTop:20}}><span className="step-num">2</span> פרטי השחקן/ית</div>
              <div style={{color:'#8a9aba', fontSize:'0.85rem', marginTop:6, marginBottom:4}}>כדי שנוכל לעצב לשחקן את קלף הכוכבים האישי שלו, ספרו לנו עליו:</div>
              <div className="form-row">
                <div className="form-group">
                  <label>שם פרטי *</label>
                  <input name="child_first_name" value={form.child_first_name} onChange={handle} required placeholder="שם פרטי" />
                </div>
                <div className="form-group">
                  <label>שם משפחה *</label>
                  <input name="child_last_name" value={form.child_last_name} onChange={handle} required placeholder="שם משפחה" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>תאריך לידה <span style={{color:'#8a9aba', fontWeight:400, fontSize:'0.82rem'}}>(שנה — חובה)</span></label>
                  {/* RTL — שנה מימין, חודש באמצע, יום משמאל */}
                  <div style={{display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr', gap:8}}>
                    <input name="child_dob_year" value={form.child_dob_year} onChange={handle} required
                      placeholder="שנה *" type="number" min="2000" max="2020" style={{textAlign:'center'}} />
                    <input name="child_dob_month" value={form.child_dob_month} onChange={handle}
                      placeholder="חודש" type="number" min="1" max="12" style={{textAlign:'center'}} />
                    <input name="child_dob_day" value={form.child_dob_day} onChange={handle}
                      placeholder="יום" type="number" min="1" max="31" style={{textAlign:'center'}} />
                  </div>
                  <div style={{color:'#8a9aba', fontSize:'0.8rem', marginTop:6}}>נדרש כדי לעצב את הקלף ולהתאים תכנים לשכבת הגיל של הילד</div>
                </div>
              </div>

              {/* מועדון */}
              <div className="form-group" style={{marginTop:4}}>
                <label>מועדון כדורגל *</label>
                <TeamCombobox value={form.team} onChange={val => setForm(f => ({ ...f, team: val }))} />
              </div>

              {/* ── תפקידים במגרש ── */}
              <div className="form-group" style={{marginTop:16}}>
                <label style={{marginBottom:8, display:'block'}}>תפקיד/ים במגרש <span style={{color:'#8a9aba', fontWeight:400, fontSize:'0.85rem'}}>(בחר עד 3)</span></label>
                <div style={{display:'flex', flexWrap:'wrap', gap:10}}>
                  {POSITIONS.map(pos => {
                    const selected = positions.includes(pos);
                    return (
                      <button key={pos} type="button" onClick={() => togglePosition(pos)}
                        style={{
                          padding:'8px 18px', borderRadius:20,
                          border: selected ? '2px solid #d4af37' : '2px solid #2a3a6a',
                          background: selected ? 'linear-gradient(135deg,#d4af37,#f0cc60)' : '#1a2a4a',
                          color: selected ? '#0a1120' : '#ccc',
                          fontWeight: selected ? 700 : 400,
                          cursor: positions.length >= 3 && !selected ? 'not-allowed' : 'pointer',
                          fontSize:'0.95rem', fontFamily:"'Heebo', sans-serif",
                          opacity: positions.length >= 3 && !selected ? 0.5 : 1, transition:'all 0.2s',
                        }}>
                        {selected ? '✓ ' : ''}{pos}
                      </button>
                    );
                  })}
                </div>
                {positions.length === 3 && <p style={{color:'#d4af37', fontSize:'0.82rem', marginTop:6}}>✓ בחרת 3 תפקידים (המקסימום)</p>}
              </div>

              {/* ── אישורים ── */}
              <div className="checkbox-group" style={{marginTop:20}}>
                <label>
                  <input type="checkbox" checked={agreed1} onChange={e=>setAgreed1(e.target.checked)} />
                  ✅ אני מאשר/ת שאני הורה מעל גיל 18 של השחקן/ית שפרטיו מולאו בטופס
                </label>
                <label>
                  <input type="checkbox" checked={agreed2} onChange={e=>setAgreed2(e.target.checked)} />
                  <span>
                    ✅ אני מאשר/ת לקבל עדכונים על הטבות, מבצעים ותכנים בלעדיים של מועדון ההטבות של יוסטאר לכתובת האימייל ולוואטסאפ האישי שמסרתי.
                    <br/><span style={{fontSize:'0.82rem', color:'#aab', marginTop:4, display:'block'}}>* תמיד אפשר לבקש להפסיק את החברות בליגת הכוכבים של יוסטאר</span>
                  </span>
                </label>
              </div>

              <button className="btn btn-gold" type="submit" disabled={loading} style={{width:'100%', marginTop:24, fontSize:'1.1rem', lineHeight:1.5}}>
                {loading ? '...שולח' : (
                  <span style={{display:'flex', flexDirection:'column', alignItems:'center', gap:2}}>
                    <span>⭐ אני רוצה את קלף הכוכבים לילד שלי</span>
                    <span style={{fontSize:'0.85rem', fontWeight:700, opacity:0.9, marginTop:2}}>{JOIN_LINE}</span>
                  </span>
                )}
              </button>

              {/* הודעה מתחת לכפתור השליחה */}
              <div style={{marginTop:16, background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.25)', borderRadius:10, padding:'12px 16px', color:'#c0cce6', fontSize:'0.86rem', lineHeight:1.7, textAlign:'center'}}>
                💬 מיד בסיום ההרשמה תוכלו לשלוח לנו וואטסאפ עם תמונה של השחקן/ית — ונתחיל לתאם את הקלף, הכתובת והמשלוח.
                <br/><span style={{color:'#8a9aba'}}>זמן אספקה עד חודש מאישור העיצוב.</span>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ===== FOOTER (מצומצם) ===== */}
      <footer style={{background:'#040a18', borderTop:'2px solid #1a2a4a', padding:'52px 20px 28px', direction:'rtl'}}>
        <div style={{maxWidth:960, margin:'0 auto'}}>

          <div style={{textAlign:'center', marginBottom:40}}>
            <div style={{color:'#d4af37', fontWeight:900, fontSize:'clamp(1.2rem,3vw,1.5rem)', marginBottom:8}}>⭐ ליגת הכוכבים של יוסטאר</div>
            <div style={{color:'#6a7a9a', fontSize:'0.9rem', lineHeight:1.8, maxWidth:560, margin:'0 auto 20px'}}>
              הבית של משפחות שחיות כדורגל — מלווים שחקנים ומשפחותיהם כבר מעל 16 שנים.
            </div>
            <a href="#register" className="btn btn-gold" style={{fontSize:'0.9rem', padding:'10px 28px'}}>
              ⭐ הצטרפו למועדון בחינם
            </a>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:32, marginBottom:40, textAlign:'right'}}>

            {/* עמודה 1 — הטבות */}
            <div>
              <div style={{color:'#d4af37', fontWeight:700, fontSize:'0.95rem', marginBottom:14, borderBottom:'1px solid #1a2a4a', paddingBottom:8}}>הטבות חברי המועדון</div>
              {[
                '🎁 קלף הכוכבים האישי במתנה (דמי משלוח בלבד — 20 ₪)',
                '📲 תכנים ומבצעים בלעדיים לוואטסאפ',
              ].map(b => (
                <div key={b} style={{color:'#6a7a9a', fontSize:'0.85rem', marginBottom:9, lineHeight:1.6}}>{b}</div>
              ))}
            </div>

            {/* עמודה 2 — צרו קשר */}
            <div>
              <div style={{color:'#d4af37', fontWeight:700, fontSize:'0.95rem', marginBottom:14, borderBottom:'1px solid #1a2a4a', paddingBottom:8}}>צרו קשר</div>
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                <a href="https://wa.me/9720546318704" target="_blank" rel="noopener noreferrer"
                  style={{display:'flex', alignItems:'center', gap:10, color:'#25d366', textDecoration:'none', fontSize:'0.88rem', fontWeight:600}}>
                  {WA_ICON} וואטסאפ יוסטאר
                </a>
                <a href="tel:0546318704"
                  style={{display:'flex', alignItems:'center', gap:10, color:'#8a9aba', textDecoration:'none', fontSize:'0.88rem'}}>
                  {PHONE_ICON} 054-6318704
                </a>
                <a href="https://www.youtube.com/@ustarsoccer2972" target="_blank" rel="noopener noreferrer"
                  style={{display:'flex', alignItems:'center', gap:10, color:'#e00', textDecoration:'none', fontSize:'0.88rem'}}>
                  {YT_ICON} ערוץ היוטיוב של יוסטאר
                </a>
              </div>
            </div>
          </div>

          {/* שורת קישורי טקסט */}
          <div style={{borderTop:'1px solid #1a2a4a', paddingTop:20, marginBottom:16, display:'flex', flexWrap:'wrap', justifyContent:'center', alignItems:'center', gap:'6px 14px', textAlign:'center'}}>
            {[
              { label:'מוצרי יוסטאר', href:'https://wa.me/9720546318704' },
              { label:'ערוץ היוטיוב', href:'https://www.youtube.com/@ustarsoccer2972' },
              { label:'וואטסאפ', href:'https://wa.me/9720546318704' },
              { label:'054-6318704', href:'tel:0546318704' },
            ].map((l, i, arr) => (
              <React.Fragment key={l.label}>
                <a href={l.href} target={l.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                  style={{color:'#8a9aba', textDecoration:'none', fontSize:'0.85rem'}}>{l.label}</a>
                {i < arr.length - 1 && <span style={{color:'#2e3d58'}}>·</span>}
              </React.Fragment>
            ))}
          </div>

          <div style={{borderTop:'1px solid #1a2a4a', paddingTop:20, display:'flex', flexDirection:'column', alignItems:'center', gap:8, textAlign:'center'}}>
            <a href="/golcut" style={{color:'#3a4a6a', fontSize:'0.82rem', textDecoration:'none'}}>סטודיו קליפים</a>
            <span style={{color:'#3a4a6a', fontSize:'0.82rem'}}>© {new Date().getFullYear()} יוסטאר — כל הזכויות שמורות</span>
            <span style={{color:'#2e3d58', fontSize:'0.78rem', lineHeight:1.6}}>
              האתר כתוב בלשון זכר מטעמי נוחות בלבד ופונה בשווה לשחקנים ושחקניות כדורגל ולהוריהם.
            </span>
          </div>

        </div>
      </footer>
    </div>
  );
}
